import React, { useState, useEffect, useRef, useCallback } from 'react';
import { executeRawQuery } from '../services/api';
import { substituteVars } from '../utils/sqlVariables';
import ResultsTable from './ResultsTable';
import DrillDownModal from './DrillDownModal';

function Widget({ widget, onOpenSettings, onDelete, onClone, onResize, templateVars, onDragStart, onDragOver, onDrop }) {
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillDown, setDrillDown] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [resizing, setResizing] = useState(false);
  const menuRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const resolvedSql = substituteVars(widget.sql, templateVars);
    executeRawQuery(resolvedSql)
      .then(data => {
        if (!cancelled) setResults(data);
      })
      .catch(err => {
        if (!cancelled) setError(err.message || 'Query failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [widget.sql, templateVars]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const card = cardRef.current;
    if (!card) return;

    const grid = card.parentElement;
    const gridRect = grid.getBoundingClientRect();
    const gridStyles = window.getComputedStyle(grid);
    const gap = parseFloat(gridStyles.gap) || 16;
    const colWidth = (gridRect.width - gap * 5) / 6;
    const rowHeight = 150;
    const cellW = colWidth + gap;
    const cellH = rowHeight + gap;

    const cardRect = card.getBoundingClientRect();
    const cardLeft = cardRect.left;
    const cardTop = cardRect.top;

    setResizing(true);

    const onMouseMove = (ev) => {
      const width = ev.clientX - cardLeft;
      const height = ev.clientY - cardTop;
      const newCols = Math.max(1, Math.min(6, Math.round((width + gap / 2) / cellW)));
      const newRows = Math.max(1, Math.min(6, Math.round((height + gap / 2) / cellH)));
      card.style.gridColumn = `span ${newCols}`;
      card.style.gridRow = `span ${newRows}`;
      card.dataset.pendingCols = newCols;
      card.dataset.pendingRows = newRows;
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      setResizing(false);
      const cols = parseInt(card.dataset.pendingCols, 10);
      const rows = parseInt(card.dataset.pendingRows, 10);
      if (cols && rows && onResize && (cols !== widget.colSpan || rows !== widget.rowSpan)) {
        onResize(widget.id, cols, rows);
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [widget.id, widget.colSpan, widget.rowSpan, onResize]);

  const isMetric = widget.chartType === 'metric';
  const colSpan = widget.colSpan || (isMetric ? 1 : 2);
  const rowSpan = widget.rowSpan || (isMetric ? 1 : 2);

  return (
    <div
      ref={cardRef}
      className={`widget-card${isMetric ? ' widget-card-metric' : ''}${resizing ? ' widget-resizing' : ''}`}
      style={{ gridColumn: `span ${colSpan}`, gridRow: `span ${rowSpan}` }}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', widget.id);
        if (onDragStart) onDragStart(widget.id);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (onDragOver) onDragOver(widget.id);
      }}
      onDrop={(e) => {
        e.preventDefault();
        if (onDrop) onDrop(widget.id);
      }}
    >
      <div className="widget-card-header">
        <span className="widget-card-title">{widget.title}</span>
        <div className="widget-card-actions" ref={menuRef}>
          <button className="widget-menu-btn" onClick={() => setMenuOpen(prev => !prev)} title="Menu">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5" r="2"/>
              <circle cx="12" cy="12" r="2"/>
              <circle cx="12" cy="19" r="2"/>
            </svg>
          </button>
          {menuOpen && (
            <div className="widget-menu-dropdown">
              <button onClick={() => { setMenuOpen(false); onOpenSettings(widget); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                Edit
              </button>
              <button onClick={() => { setMenuOpen(false); onClone(widget); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                Clone
              </button>
              <button className="widget-menu-danger" onClick={() => { setMenuOpen(false); onDelete(widget.id); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                Remove
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="widget-card-body">
        {loading && <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>Loading...</div>}
        {error && <div className="widget-card-error">{error}</div>}
        {results && (
          <ResultsTable
            key={widget.chartType}
            results={results}
            defaultViewMode={widget.chartType}
            hideViewToggle
            onChartClick={isMetric ? undefined : (clickContext) => setDrillDown(clickContext)}
            seriesColors={widget.seriesColors}
            unit={widget.unit}
          />
        )}
      </div>
      <div className="widget-resize-handle" onMouseDown={handleResizeStart}>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M10 2L2 10"/>
          <path d="M10 6L6 10"/>
          <path d="M10 10L10 10"/>
        </svg>
      </div>
      {drillDown && (
        <DrillDownModal
          isOpen={!!drillDown}
          onClose={() => setDrillDown(null)}
          originalSql={widget.sql}
          clickContext={drillDown}
          templateVars={templateVars}
        />
      )}
    </div>
  );
}

export default Widget;
